import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { money } from "@/features/inventory/constants";
import type { QualityStatus } from "@/features/inventory/types";
import type { QcQueueRow } from "@/features/quality/types";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

export function useQcQueue(statusFilter: "open" | QualityStatus | "all" = "open") {
  return useQuery({
    queryKey: ["quality_queue", statusFilter],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<QcQueueRow[]> => {
      let q = supabase
        .from("inventory_batches")
        .select(
          `id, batch_no, material_id, quantity, weight, quality_status, batch_status,
          received_at, qc_notes, inspected_at, purchase_order_id,
          materials(name),
          material_grades(name),
          shape_sizes(size_label),
          stock_locations(code, name)`,
        )
        .eq("batch_status", "in_stock")
        .order("received_at", { ascending: false })
        .limit(200);

      if (statusFilter === "open") {
        q = q.in("quality_status", ["pending", "hold"]);
      } else if (statusFilter !== "all") {
        q = q.eq("quality_status", statusFilter);
      }

      const { data, error } = await q;
      if (error) throw error;

      const rows = data ?? [];
      const ids = rows.map((r) => String((r as { id: string }).id));

      const sourceByBatch = new Map<
        string,
        { plan_no: string | null; process_name: string | null; source: QcQueueRow["source"] }
      >();

      if (ids.length) {
        const { data: outs } = await supabase
          .from("process_stage_run_outputs")
          .select(
            `inventory_batch_id,
            process_stage_runs(
              process_plans(plan_no),
              process_plan_stages(process_types(name))
            )`,
          )
          .in("inventory_batch_id", ids);

        for (const row of outs ?? []) {
          const raw = row as Record<string, unknown>;
          const batchId = String(raw.inventory_batch_id);
          // PostgREST may return object or array for many-to-one embeds
          const runRaw = raw.process_stage_runs;
          const run = (Array.isArray(runRaw) ? runRaw[0] : runRaw) as {
            process_plans?: { plan_no?: string } | { plan_no?: string }[] | null;
            process_plan_stages?:
              | { process_types?: { name?: string } | null }
              | { process_types?: { name?: string } | null }[]
              | null;
          } | null;
          const plan = run?.process_plans;
          const planObj = Array.isArray(plan) ? plan[0] : plan;
          const stage = run?.process_plan_stages;
          const stageObj = Array.isArray(stage) ? stage[0] : stage;
          const pt = stageObj?.process_types;
          sourceByBatch.set(batchId, {
            plan_no: planObj?.plan_no ?? null,
            process_name: pt?.name ?? null,
            source: "production",
          });
        }
      }

      return rows.map((row) => {
        const raw = row as Record<string, unknown>;
        const id = String(raw.id);
        const mat = raw.materials as { name?: string } | null;
        const grade = raw.material_grades as { name?: string } | null;
        const shape = raw.shape_sizes as { size_label?: string } | null;
        const loc = raw.stock_locations as { code?: string; name?: string } | null;
        const src = sourceByBatch.get(id);
        return {
          id,
          batch_no: String(raw.batch_no ?? ""),
          material_id: String(raw.material_id),
          material_name: mat?.name ?? null,
          grade_name: grade?.name ?? null,
          shape_label: shape?.size_label ?? null,
          location_code: loc?.code ?? null,
          location_name: loc?.name ?? null,
          quantity: money(raw.quantity),
          weight: money(raw.weight),
          quality_status: raw.quality_status as QualityStatus,
          batch_status: String(raw.batch_status),
          received_at: String(raw.received_at),
          qc_notes: (raw.qc_notes as string | null) ?? null,
          inspected_at: (raw.inspected_at as string | null) ?? null,
          plan_no: src?.plan_no ?? null,
          process_name: src?.process_name ?? null,
          source:
            src?.source ??
            (raw.purchase_order_id ? "receipt" : "other"),
        };
      });
    },
  });
}

export function useVerifyBatchQuality() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      batchId,
      quality_status,
      qc_notes,
    }: {
      batchId: string;
      quality_status: QualityStatus;
      qc_notes?: string;
    }) => {
      const { data: existing, error: exErr } = await supabase
        .from("inventory_batches")
        .select("id, quality_status, batch_status")
        .eq("id", batchId)
        .single();
      if (exErr) throw exErr;
      if (existing.batch_status !== "in_stock") {
        throw new Error("Only in-stock batches can be verified");
      }

      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("inventory_batches")
        .update({
          quality_status,
          qc_notes: qc_notes?.trim() || null,
          inspected_by: user?.id ?? null,
          inspected_at: now,
          updated_at: now,
        })
        .eq("id", batchId)
        .select("id, batch_no, quality_status")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["quality_queue"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches"] });
      toast.success(`${data.batch_no} → ${data.quality_status}`);
    },
    onError: (error: Error) => toast.error(error.message || "QC update failed"),
  });
}
